import { CSSProperties, ReactNode } from "react";
import {
  AccordionControl,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Text,
  ThemeIcon,
  useAccordionContext,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";

// Accordion values of the review sections, also used to fold or unfold them all at once
export const REVIEW_SECTIONS = {
  patient: "patient",
  products: "products",
  files: "files",
  commentAndDelivery: "commentAndDelivery",
} as const;

export type ReviewSection =
  (typeof REVIEW_SECTIONS)[keyof typeof REVIEW_SECTIONS];

// Paper's withBorder uses Mantine's default gray, which themev2 does not override
export const CARD_BORDER_STYLE = {
  "--paper-border-color": "var(--mantine-color-ink-1)",
} as CSSProperties;

export const ALL_REVIEW_SECTIONS = Object.values(REVIEW_SECTIONS);

export const ReviewCard = ({
  value,
  index,
  title,
  count,
  summary,
  onEdit,
  children,
}: {
  value: ReviewSection;
  index: number;
  title: string;
  count?: number;
  summary?: string;
  onEdit?: () => void;
  children?: ReactNode;
}) => {
  const { isItemActive } = useAccordionContext();
  const isOpen = isItemActive(value);

  return (
    <Paper withBorder radius="lg" style={CARD_BORDER_STYLE}>
      <AccordionItem value={value}>
        <Group wrap="nowrap">
          <Box flex={1} miw={0}>
            <AccordionControl>
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" radius="xl" size="sm" aria-hidden>
                  <Text span variant="numeric" c="inherit">
                    {index}
                  </Text>
                </ThemeIcon>
                <Text variant="strong">{title}</Text>
                {!!count && isOpen && (
                  <Badge variant="light" size="sm">
                    {count}
                  </Badge>
                )}
                {!isOpen && summary && (
                  <Text size="sm" c="dimmed" truncate="end">
                    {summary}
                  </Text>
                )}
              </Group>
            </AccordionControl>
          </Box>
          {onEdit && (
            <Button
              variant="subtle"
              size="xs"
              mr="md"
              flex="none"
              leftSection={<IconPencil size={14} />}
              onClick={onEdit}
            >
              Modifier
            </Button>
          )}
        </Group>
        <AccordionPanel>{children}</AccordionPanel>
      </AccordionItem>
    </Paper>
  );
};
